import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, articles } from '@/mocks/qieziData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const video = videos.find((v) => v.id === id);
  const article = articles.find((a) => a.id === id);

  const relatedVideos = videos.filter((v) => v.id !== id).slice(0, 3);

  if (!video && !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-500 text-lg">内容未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (video) {
    const videoJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.description,
      thumbnailUrl: video.thumbnail,
      uploadDate: video.publishDate,
      author: {
        '@type': 'Person',
        name: video.author,
      },
      interactionStatistic: [
        { '@type': 'InteractionCounter', interactionType: 'https://schema.org/WatchAction', userInteractionCount: parseInt(video.views.replace(/[^0-9]/g, '')) * 10000 },
        { '@type': 'InteractionCounter', interactionType: 'https://schema.org/LikeAction', userInteractionCount: parseInt(video.likes.replace(/[^0-9]/g, '')) * 10000 },
      ],
    };

    const categoryName = (() => {
      const cats: Record<string, string> = { comedy: '搞笑视频', lifestyle: '生活记录', talent: '才艺展示', food: '美食分享', travel: '旅行风光', music: '音乐舞蹈', pet: '萌宠日常', sports: '运动健身', tech: '科技数码', fashion: '时尚穿搭', game: '游戏电竞', knowledge: '知识科普' };
      return cats[video.category] || video.category;
    })();

    return (
      <>
        <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <main className="pt-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '视频分类', path: '/category' },
                  { label: categoryName, path: `/category?cat=${video.category}` },
                  { label: video.title },
                ]}
              />

              <article>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-4">
                  {video.title}
                </h1>

                <div className="flex items-center gap-3 mt-4 pb-6 border-b border-background-200/70">
                  <img src={video.avatar} alt={video.author} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{video.author}</p>
                    <p className="text-xs text-foreground-500">{video.publishDate} 发布</p>
                  </div>
                  <div className="ml-auto flex items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i> {video.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i> {video.likes}
                    </span>
                  </div>
                </div>

                {/* Video Player Area */}
                <div className="mt-6 relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-900">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <button className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-white transition-all hover:scale-110">
                      <i className="ri-play-fill text-primary-500 text-2xl ml-1"></i>
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6 py-4">
                  <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 flex-wrap mt-2 pb-6 border-b border-background-200/70">
                  <Link
                    to={`/category?cat=${video.category}`}
                    className="px-3 py-1 bg-primary-50 text-primary-600 text-xs rounded-full cursor-pointer hover:bg-primary-100 transition-colors whitespace-nowrap"
                  >
                    {categoryName}
                  </Link>
                  <span className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap">短视频</span>
                  <span className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap">热门推荐</span>
                </div>

                {/* Share */}
                <div className="flex items-center gap-4 mt-6">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              </article>

              {/* Related Videos */}
              <section className="mt-12 pb-14">
                <h3 className="text-lg font-bold text-foreground-900 mb-4">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/article/${rv.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] border border-background-200/70"
                    >
                      <div className="relative w-full aspect-video overflow-hidden">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{rv.duration}</div>
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">{rv.title}</h4>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-foreground-500">{rv.author}</span>
                          <span className="text-xs text-foreground-400">{rv.views}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </main>

          <Footer />
        </div>
      </>
    );
  }

  if (article) {
    const articleJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
      image: article.thumbnail,
      publisher: {
        '@type': 'Organization',
        name: '茄子视频',
      },
    };

    return (
      <>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <main className="pt-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '视频分类', path: '/category' },
                  { label: article.title },
                ]}
              />

              <article>
                <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full mb-4">
                  知识科普
                </span>

                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 mt-4 pb-6 border-b border-background-200/70">
                  <img src={article.authorAvatar} alt={article.author} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                    <p className="text-xs text-foreground-500">{article.publishDate} · 阅读时间 {article.readTime}</p>
                  </div>
                </div>

                <p className="mt-6 text-sm text-foreground-600 leading-relaxed bg-background-100 rounded-lg p-4">
                  <strong className="text-foreground-900">摘要：</strong>{article.summary}
                </p>

                <div className="mt-6 aspect-video rounded-lg overflow-hidden">
                  <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                </div>

                <div className="mt-8 space-y-6 text-sm text-foreground-700 leading-relaxed">
                  {article.content.map((block, idx) => {
                    if (block.type === 'heading') {
                      return <h2 key={idx} className="text-lg font-bold text-foreground-900 mt-8 mb-3">{block.text}</h2>;
                    }
                    return <p key={idx}>{block.text}</p>;
                  })}
                </div>

                <div className="flex items-center gap-4 mt-10 pt-6 border-t border-background-200/70">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors">
                    <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              </article>

              <section className="mt-12 pb-14">
                <h3 className="text-lg font-bold text-foreground-900 mb-4">推荐阅读</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles.filter((a) => a.id !== id).map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group flex gap-3 p-3 rounded-lg bg-background-100 cursor-pointer transition-all hover:bg-primary-50"
                    >
                      <img src={ra.thumbnail} alt={ra.title} className="w-24 h-16 rounded object-cover flex-shrink-0" />
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">{ra.title}</h4>
                        <p className="text-xs text-foreground-400 mt-1">{ra.publishDate} · {ra.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </main>

          <Footer />
        </div>
      </>
    );
  }

  return null;
}